import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Bot, Pencil, Mic, BarChart3, BookOpen, Brain,
  ChevronRight, ArrowRight, Check, Quote, Headphones, GraduationCap, Globe,
} from "lucide-react";
/**
 * LANDING PREVIEW — EnglishTest.UK
 * Daylight Light theme (near-white cream + navy text + solid orange buttons + glow + click sound)
 * Top header: brand only on the left + Sign In / Sign Up CTAs on the right (no other nav).
 * Footer: all secondary links.
 *
 * Available at /design-preview/landing
 */

// ============================================================
// Tokens (Daylight Light)
// ============================================================
const T = {
  bg: "#FCFAF3",
  bg2: "#F5F1E0",
  cardBg: "#FFFFFF",
  cardBgWarm: "#FBF6E6",
  border: "#E5DCC0",
  borderSoft: "#EEE6CE",
  input: "#FFFFFF",
  primary: "#F77F00",
  primaryDark: "#D26900",
  primaryGlow: "rgba(247,127,0,0.45)",
  primaryGlowSoft: "rgba(247,127,0,0.22)",
  secondary: "#D62828",
  text: "#003049",
  textMuted: "rgba(0,48,73,0.65)",
  textOnPrimary: "#FFFFFF",
  cream: "#EAE2B7",
  success: "#0C4B66",
};

// ============================================================
// Click sound (Web Audio API — no asset)
// ============================================================
function useClickSound() {
  const ctxRef = useRef(null);
  const getCtx = () => {
    if (!ctxRef.current) {
      try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); }
      catch (e) { return null; }
    }
    return ctxRef.current;
  };
  return (variant = "primary") => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(4000, now);
    if (variant === "primary") {
      osc.type = "square";
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.06);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    } else {
      osc.type = "sine";
      osc.frequency.setValueAtTime(1200, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.04, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    }
    osc.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.3);
  };
}

// ============================================================
// Buttons
// ============================================================
const PrimaryBtn = ({ children, to, className = "", size = "md", onClick, ...rest }) => {
  const play = useClickSound();
  const sizes = {
    sm: "h-10 px-5 text-xs",
    md: "h-12 px-6 text-sm",
    lg: "h-14 px-8 text-sm",
    xl: "h-16 px-10 text-base",
  };
  const Comp = to ? Link : "button";
  return (
    <Comp
      to={to}
      onClick={(e) => { play("primary"); onClick && onClick(e); }}
      className={`relative inline-flex items-center justify-center gap-2 ${sizes[size]} font-bold uppercase tracking-[0.22em] rounded-sm transition-all duration-300 active:scale-[0.97] ${className}`}
      style={{
        background: T.primary,
        color: T.textOnPrimary,
        border: `2px solid ${T.primary}`,
        boxShadow: `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)`,
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = T.primaryDark;
        e.currentTarget.style.borderColor = T.primaryDark;
        e.currentTarget.style.boxShadow = `0 0 34px ${T.primaryGlow}, inset 0 0 14px rgba(255,255,255,0.28)`;
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = T.primary;
        e.currentTarget.style.borderColor = T.primary;
        e.currentTarget.style.boxShadow = `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)`;
        e.currentTarget.style.transform = "";
      }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

const OutlineBtn = ({ children, to, className = "", size = "md", onClick, ...rest }) => {
  const play = useClickSound();
  const sizes = {
    sm: "h-10 px-5 text-xs",
    md: "h-12 px-6 text-sm",
    lg: "h-14 px-8 text-sm",
  };
  const Comp = to ? Link : "button";
  return (
    <Comp
      to={to}
      onClick={(e) => { play("primary"); onClick && onClick(e); }}
      className={`inline-flex items-center justify-center gap-2 ${sizes[size]} font-bold uppercase tracking-[0.22em] rounded-sm transition-all duration-300 active:scale-[0.97] ${className}`}
      style={{
        background: "transparent",
        color: T.text,
        border: `2px solid ${T.text}`,
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = T.text;
        e.currentTarget.style.color = "#FFFFFF";
        e.currentTarget.style.boxShadow = `0 0 16px rgba(0,48,73,0.25)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = T.text;
        e.currentTarget.style.boxShadow = "none";
      }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

// ============================================================
// HUD frame (corner brackets)
// ============================================================
const HudFrame = ({ children, className = "", style = {}, color = T.primary }) => (
  <div className={`relative ${className}`} style={style}>
    <span className="absolute -top-1 -left-1 w-3.5 h-3.5 border-t-2 border-l-2" style={{ borderColor: color }} />
    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 border-t-2 border-r-2" style={{ borderColor: color }} />
    <span className="absolute -bottom-1 -left-1 w-3.5 h-3.5 border-b-2 border-l-2" style={{ borderColor: color }} />
    <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 border-b-2 border-r-2" style={{ borderColor: color }} />
    {children}
  </div>
);

// ============================================================
// Subtle grid background
// ============================================================
const GridBG = ({ intensity = 0.06 }) => (
  <div className="absolute inset-0 pointer-events-none" aria-hidden>
    <div className="absolute inset-0" style={{
      backgroundImage: `linear-gradient(${T.text} 1px, transparent 1px), linear-gradient(90deg, ${T.text} 1px, transparent 1px)`,
      backgroundSize: "64px 64px",
      opacity: intensity,
    }} />
    <div className="absolute inset-0" style={{
      backgroundImage: `radial-gradient(circle at 22% 22%, ${T.primaryGlowSoft}, transparent 45%), radial-gradient(circle at 80% 80%, rgba(0,48,73,0.06), transparent 50%)`
    }} />
  </div>
);

// ============================================================
// Data
// ============================================================
const TESTS = [
  { name: "IELTS Academic",   tag: "Bands 6.0–9.0",  desc: "University admissions"   },
  { name: "IELTS General",    tag: "Bands 5.5–9.0",  desc: "Immigration & work"      },
  { name: "PTE Academic",     tag: "Score 30–90",    desc: "AI-scored, fast results" },
  { name: "Oxford ELLT",      tag: "Levels A1–C2",   desc: "Oxford-style proficiency"},
  { name: "Duolingo English", tag: "Score 10–160",   desc: "Online, on-demand"       },
];

const FEATURES = [
  { Icon: Mic, title: "AI Speaking Examiner", desc: "Real exam simulation with fluency, pronunciation and coherence scoring." },
  { Icon: Pencil, title: "AI Writing Checker", desc: "Task 1 / Task 2 graded with full IELTS band descriptors and rewrites." },
  { Icon: Brain, title: "Predicted Band Score", desc: "Continuous prediction tuned on every attempt and every skill." },
  { Icon: BarChart3, title: "Weakness Heatmaps", desc: "Visualise where every mark is being lost across all four skills." },
  { Icon: Bot, title: "AI Coach 24/7", desc: "Chat anywhere in the platform with an examiner-grade tutor." },
  { Icon: BookOpen, title: "Adaptive Study Plans", desc: "Personalised, day-by-day plans built around your target band." },
];

const TESTIMONIALS = [
  { name: "Aarav S.", role: "IELTS 8.0 · UK", body: "From 6.5 to 8.0 in 8 weeks. The AI writing feedback caught a comma habit no teacher noticed." },
  { name: "Mei L.",   role: "PTE 79 · Australia", body: "Speaking simulator felt like the real test. Predicted score was within 2 points of my actual." },
  { name: "Diana K.", role: "Duolingo 145 · USA", body: "Adaptive practice + live mentors is unbeatable. Worth every cent." },
];

// ============================================================
// LANDING
// ============================================================
export default function Landing() {
  const [time, setTime] = useState(getTimeLeft());
  useEffect(() => {
    const id = "lingoq-preview-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
    const t = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="landing-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
        .reveal { opacity: 0; transform: translateY(16px); transition: opacity .8s ease, transform .8s ease; }
        .reveal.in { opacity: 1; transform: none; }
      `}</style>

      {/* ============================================================
          HEADER — brand left, Sign In / Sign Up only on the right.
          NO other nav links (rest will live in the footer).
      ============================================================ */}
      <header className="sticky top-0 z-40 backdrop-blur-md border-b" style={{ background: `${T.bg}E6`, borderColor: T.border }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-sm border-2 flex items-center justify-center display font-bold text-lg transition-all"
              style={{ borderColor: T.primary, color: T.primary, boxShadow: `0 0 14px ${T.primaryGlow}` }}>
              E
            </div>
            <div>
              <div className="display font-bold text-xl tracking-tight" style={{ color: T.text }}>EnglishTest<span style={{ color: T.primary }}>.UK</span></div>
              <div className="mono text-[9px] tracking-[0.35em] mt-0.5" style={{ color: T.textMuted }}>// PASS · NOT JUST PRACTICE</div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <OutlineBtn to="/login" size="md" data-testid="header-login-btn">SIGN IN</OutlineBtn>
            <PrimaryBtn to="/signup" size="md" data-testid="header-signup-btn">
              SIGN UP <ArrowRight className="w-4 h-4" />
            </PrimaryBtn>
          </div>
        </div>
      </header>

      {/* ============================================================
          HERO
      ============================================================ */}
      <section className="relative overflow-hidden">
        <GridBG />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          {/* LEFT */}
          <div className="reveal in">
            <div className="inline-flex items-center gap-3 mono text-sm md:text-base tracking-[0.22em] px-4 py-2.5 rounded-sm border font-bold" style={{ borderColor: T.border, color: T.text, background: T.cardBgWarm }}>
              <Sparkles className="w-4 h-4 shrink-0" style={{ color: T.primary }} />
              <span style={{ color: T.primary }}>AI</span>
              <span className="opacity-40">·</span>
              <span>IELTS</span>
              <span className="opacity-40">·</span>
              <span>PTE</span>
              <span className="opacity-40">·</span>
              <span>OXFORD ELLT</span>
              <span className="opacity-40">·</span>
              <span>DUOLINGO</span>
            </div>
            <h1 className="display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] mt-6" style={{ color: T.text }}>
              AI-ENABLED<br />
              <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>ENGLISH LANGUAGE</span><br />
              TEST PREPARATION SYSTEM
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 mono text-base md:text-lg tracking-[0.18em] uppercase font-semibold" style={{ color: T.text }}>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: T.primary, boxShadow: `0 0 10px ${T.primaryGlow}` }} />
                Subscribe to AI Mocks
              </span>
              <span className="text-2xl md:text-3xl leading-none" style={{ color: T.primary }}>/</span>
              <span className="whitespace-nowrap">Join Online Hybrid Classes</span>
            </div>
            <p className="text-base md:text-lg leading-relaxed mt-5 max-w-xl" style={{ color: T.textMuted }}>
              Two ways to train: AI-graded self-practice with real-time examiners and band predictions, or live online hybrid classes with certified IELTS, PTE, Oxford ELLT and Duolingo tutors.
            </p>

            <div className="mt-9">
              <PrimaryBtn to="/free-assessment" size="xl" data-testid="hero-start-btn" className="!h-[68px] !px-12 !text-base group">
                <Sparkles className="w-5 h-5 transition-transform group-hover:rotate-12" />
                START YOUR FREE ASSESSMENT
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </PrimaryBtn>
              <div className="mono text-[10px] tracking-[0.3em] mt-3" style={{ color: T.textMuted }}>
                // NO CREDIT CARD · 5-MIN AI BAND ESTIMATE · INSTANT RESULT
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {[
                ["27,400+", "RECRUITS"],
                ["±0.5",    "BAND PRECISION"],
                ["24/7",    "AI EXAMINERS"],
              ].map(([n, l], i) => (
                <HudFrame key={i} className="p-3" color={T.primary}>
                  <div className="display text-2xl font-bold" style={{ color: T.text }}>{n}</div>
                  <div className="mono text-[9px] tracking-[0.3em] mt-1" style={{ color: T.primary }}>{l}</div>
                </HudFrame>
              ))}
            </div>
          </div>

          {/* RIGHT — preview card with AI-themed floating props around it */}
          <div className="reveal in relative">
            {/* Floating AI props — purely decorative, behind/around the card */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden>
              {/* Soft orange halo behind card */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full"
                style={{ background: `radial-gradient(circle, ${T.primaryGlow} 0%, transparent 55%)`, filter: "blur(28px)" }} />

              {/* --- HOLOGRAPHIC GLOBE — top-right, rotating --- */}
              <div className="absolute -top-16 -right-4 w-32 h-32 ai-float">
                <svg viewBox="0 0 120 120" className="w-full h-full ai-spin-slow" fill="none">
                  <circle cx="60" cy="60" r="50" stroke={T.primary} strokeWidth="1" opacity="0.6" />
                  {/* meridians */}
                  <ellipse cx="60" cy="60" rx="50" ry="20" stroke={T.primary} strokeWidth="1" opacity="0.4" />
                  <ellipse cx="60" cy="60" rx="50" ry="35" stroke={T.primary} strokeWidth="1" opacity="0.4" />
                  <ellipse cx="60" cy="60" rx="20" ry="50" stroke={T.primary} strokeWidth="1" opacity="0.4" />
                  <ellipse cx="60" cy="60" rx="35" ry="50" stroke={T.primary} strokeWidth="1" opacity="0.4" />
                  {/* equator */}
                  <line x1="10" y1="60" x2="110" y2="60" stroke={T.primary} strokeWidth="1.2" />
                  <line x1="60" y1="10" x2="60" y2="110" stroke={T.primary} strokeWidth="1.2" />
                  {/* candidate location pins */}
                  <circle cx="40" cy="42" r="2.5" fill={T.primary} />
                  <circle cx="78" cy="50" r="2.5" fill={T.primary} />
                  <circle cx="55" cy="80" r="2.5" fill={T.primary} />
                  <circle cx="88" cy="72" r="2.5" fill={T.primary} />
                </svg>
                <div className="absolute -bottom-2 left-0 right-0 text-center mono text-[8px] tracking-[0.3em]" style={{ color: T.primary }}>
                  · 27 COUNTRIES ·
                </div>
              </div>

              {/* --- FLOATING ENGLISH LETTERS — top-left area --- */}
              {[
                { ch: "A", x: "-2%",  y: "12%", size: 56, rotate: -8,  delay: "0s",   dur: "5s" },
                { ch: "b", x: "8%",   y: "32%", size: 40, rotate: 10,  delay: "0.6s", dur: "6s" },
                { ch: "C", x: "22%",  y: "8%",  size: 36, rotate: -4,  delay: "1.2s", dur: "5.5s" },
                { ch: "?", x: "62%",  y: "85%", size: 38, rotate: 6,   delay: "0.4s", dur: "6.5s" },
                { ch: "&", x: "85%",  y: "8%",  size: 32, rotate: -12, delay: "1.5s", dur: "5s" },
              ].map((l, i) => (
                <div key={`ltr-${i}`} className="absolute ai-float-slow font-bold select-none pointer-events-none"
                  style={{
                    left: l.x, top: l.y,
                    fontSize: `${l.size}px`,
                    color: T.primary,
                    opacity: 0.22,
                    transform: `rotate(${l.rotate}deg)`,
                    fontFamily: "'Rajdhani', sans-serif",
                    animationDelay: l.delay,
                    animationDuration: l.dur,
                    textShadow: `0 0 14px ${T.primaryGlow}`,
                  }}
                >
                  {l.ch}
                </div>
              ))}

              {/* --- AUDIO WAVEFORM — middle-left, animated bars --- */}
              <div className="absolute top-1/2 -left-8 -translate-y-1/2 flex items-center gap-1 h-10 ai-float-slow">
                {[0.4, 0.7, 0.3, 0.9, 0.5, 1.0, 0.4, 0.8, 0.3, 0.6, 0.4].map((h, i) => (
                  <span key={i} className="w-1 rounded-sm ai-wave"
                    style={{
                      height: `${h * 100}%`,
                      background: T.primary,
                      animationDelay: `${i * 0.08}s`,
                      boxShadow: `0 0 4px ${T.primaryGlow}`,
                    }} />
                ))}
              </div>

              {/* --- CODE SNIPPET CARD — bottom-right, typewriter effect via gradient --- */}
              <div className="absolute -bottom-10 -right-6 mono text-[9px] leading-[1.45] p-3 rounded-sm ai-float-rev"
                style={{ background: T.text, color: "#fff", border: `1px solid ${T.primary}`, boxShadow: `0 0 14px ${T.primaryGlow}` }}>
                <div style={{ color: T.primary }}>{">"} predict_band()</div>
                <div className="opacity-70">  fluency: <span style={{ color: T.primary }}>8.0</span></div>
                <div className="opacity-70">  lexical: <span style={{ color: T.primary }}>7.5</span></div>
                <div className="opacity-70">  grammar: <span style={{ color: T.primary }}>7.0</span></div>
                <div className="opacity-70">  pronunc: <span style={{ color: T.primary }}>8.5</span></div>
                <div style={{ color: "#FCBF49" }}>→ band: <span style={{ color: T.primary }}>7.5</span> <span className="ai-blink">▌</span></div>
              </div>

              {/* --- NEURAL NETWORK NODES — original, repositioned --- */}
              <svg className="absolute top-8 -right-2 w-44 h-44 opacity-70 ai-float-slow" viewBox="0 0 200 200" fill="none">
                <g stroke={T.primary} strokeWidth="1" opacity="0.5">
                  <line x1="30" y1="40" x2="100" y2="80" />
                  <line x1="100" y1="80" x2="170" y2="50" />
                  <line x1="100" y1="80" x2="60" y2="150" />
                  <line x1="100" y1="80" x2="160" y2="140" />
                  <line x1="30" y1="40" x2="60" y2="150" />
                  <line x1="170" y1="50" x2="160" y2="140" />
                </g>
                <g fill={T.primary}>
                  <circle cx="30" cy="40" r="4" className="ai-blink-slow" />
                  <circle cx="100" cy="80" r="6" />
                  <circle cx="170" cy="50" r="4" className="ai-blink-slow" style={{ animationDelay: "0.4s" }} />
                  <circle cx="60" cy="150" r="4" className="ai-blink-slow" style={{ animationDelay: "0.8s" }} />
                  <circle cx="160" cy="140" r="5" className="ai-blink-slow" style={{ animationDelay: "1.2s" }} />
                </g>
              </svg>

              {/* --- DATA GLYPH PILLS --- */}
              <div className="absolute -top-4 right-32 mono text-[9px] tracking-[0.25em] px-3 py-1.5 rounded-sm ai-float"
                style={{ color: T.primary, background: T.cardBg, border: `1px solid ${T.primary}`, boxShadow: `0 0 14px ${T.primaryGlow}` }}>
                <span style={{ color: T.success }} className="ai-blink">●</span> NEURAL CORE · ACTIVE
              </div>
              <div className="absolute -bottom-4 left-10 mono text-[9px] tracking-[0.25em] px-3 py-1.5 rounded-sm ai-float-rev"
                style={{ color: T.text, background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
                <span style={{ color: T.primary }}>{"<"}</span> RUBRIC v9.0 <span style={{ color: T.primary }}>{">"}</span>
              </div>
              <div className="absolute top-32 -left-2 mono text-[9px] tracking-[0.25em] px-3 py-1.5 rounded-sm ai-float"
                style={{ color: T.text, background: T.cardBg, border: `1px solid ${T.borderSoft}`, animationDelay: "0.6s" }}>
                <span style={{ color: T.primary }}>◆</span> CEFR · C2
              </div>

              {/* --- BINARY STREAM — left of card --- */}
              <div className="absolute -left-14 bottom-32 mono text-[10px] leading-tight tracking-wider opacity-40 ai-float-slow" style={{ color: T.primary }}>
                {["10110101", "01001011", "11010010", "00101110", "10111001", "11100100"].map((b, i) => (
                  <div key={i} style={{ opacity: 1 - i * 0.15 }}>{b}</div>
                ))}
              </div>

              {/* --- CIRCUIT TRACES — bottom-right corner --- */}
              <svg className="absolute -bottom-12 right-20 w-40 h-40 opacity-50" viewBox="0 0 160 160" fill="none">
                <path d="M10 80 L50 80 L50 40 L100 40 L100 100 L150 100" stroke={T.primary} strokeWidth="1.5" />
                <path d="M10 120 L70 120 L70 80 L130 80" stroke={T.primary} strokeWidth="1.5" opacity="0.5" />
                <circle cx="50" cy="40" r="3" fill={T.primary} className="ai-blink-slow" />
                <circle cx="100" cy="100" r="3" fill={T.primary} className="ai-blink-slow" style={{ animationDelay: "0.5s" }} />
                <circle cx="150" cy="100" r="5" fill={T.primary} />
              </svg>

              {/* --- ORBITAL RING — bottom-left --- */}
              <div className="absolute bottom-8 -left-10 w-20 h-20">
                <svg viewBox="0 0 80 80" className="w-full h-full ai-spin-slow" fill="none">
                  <circle cx="40" cy="40" r="34" stroke={T.primary} strokeWidth="1" strokeDasharray="2 4" opacity="0.6" />
                  <circle cx="40" cy="6" r="3" fill={T.primary} />
                  <circle cx="74" cy="40" r="2" fill={T.primary} opacity="0.6" />
                </svg>
              </div>

              {/* --- SPARKLE PARTICLES — denser --- */}
              {[
                { x: "8%",  y: "20%", d: "0s",   s: 1.5 },
                { x: "92%", y: "55%", d: "0.4s", s: 1.0 },
                { x: "18%", y: "78%", d: "0.8s", s: 1.2 },
                { x: "70%", y: "12%", d: "1.2s", s: 1.4 },
                { x: "48%", y: "3%",  d: "1.6s", s: 1.0 },
                { x: "5%",  y: "55%", d: "2.0s", s: 0.8 },
                { x: "95%", y: "85%", d: "2.4s", s: 1.2 },
                { x: "35%", y: "92%", d: "0.6s", s: 1.0 },
                { x: "62%", y: "70%", d: "1.4s", s: 1.4 },
                { x: "82%", y: "32%", d: "2.2s", s: 0.9 },
              ].map((p, i) => (
                <span key={i} className="absolute rounded-full ai-twinkle"
                  style={{
                    left: p.x, top: p.y,
                    width: `${p.s * 6}px`, height: `${p.s * 6}px`,
                    background: T.primary, boxShadow: `0 0 ${p.s * 8}px ${T.primaryGlow}`,
                    animationDelay: p.d,
                  }} />
              ))}
            </div>

            <style>{`
              @keyframes aiSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
              .ai-spin-slow { animation: aiSpin 14s linear infinite; transform-origin: center; }
              @keyframes aiTwinkle { 0%,100% { opacity: 0.25; transform: scale(0.7); } 50% { opacity: 1; transform: scale(1.2); } }
              .ai-twinkle { animation: aiTwinkle 2.4s ease-in-out infinite; }
              @keyframes aiFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
              .ai-float { animation: aiFloat 4s ease-in-out infinite; }
              .ai-float-slow { animation: aiFloat 6s ease-in-out infinite; }
              .ai-float-rev { animation: aiFloat 5s ease-in-out infinite reverse; }
              @keyframes aiPulse { 0%,100% { transform: scale(1); filter: drop-shadow(0 0 6px ${T.primaryGlow}); } 50% { transform: scale(1.08); filter: drop-shadow(0 0 20px ${T.primaryGlow}); } }
              .ai-pulse { animation: aiPulse 2.4s ease-in-out infinite; }
              @keyframes aiWave { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
              .ai-wave { transform-origin: center; animation: aiWave 0.8s ease-in-out infinite; }
              @keyframes aiBlink { 0%,49% { opacity: 1; } 50%,100% { opacity: 0.2; } }
              .ai-blink { animation: aiBlink 1s steps(1) infinite; }
              @keyframes aiBlinkSlow { 0%,80% { opacity: 1; } 90%,100% { opacity: 0.3; } }
              .ai-blink-slow { animation: aiBlinkSlow 2s ease infinite; }
            `}</style>

            {/* Live exam preview card — clean, on top */}
            <div className="relative z-10">
              <HudFrame className="p-1" color={T.primary}>
                <div className="rounded-sm overflow-hidden p-6 md:p-8" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="mono text-[10px] tracking-[0.3em]" style={{ color: T.textMuted }}>// LIVE EXAM PREVIEW</div>
                    <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.3em]" style={{ color: T.success }}>
                      <span className="w-2 h-2 rounded-full inline-block animate-pulse" style={{ background: T.success, boxShadow: `0 0 8px ${T.success}` }} />
                      ONLINE · 4 EXAMINERS
                    </div>
                  </div>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { Icon: Headphones,    l: "Listening", v: "8.0" },
                    { Icon: BookOpen,      l: "Reading",   v: "7.5" },
                    { Icon: Pencil,        l: "Writing",   v: "7.0" },
                    { Icon: Mic,           l: "Speaking",  v: "8.5" },
                  ].map((s, i) => (
                    <div key={i} className="rounded-sm p-3 border" style={{ borderColor: T.borderSoft, background: T.bg2 + "55" }}>
                      <div className="flex items-center gap-2 mono text-[10px] tracking-[0.25em]" style={{ color: T.textMuted }}>
                        <s.Icon className="w-3.5 h-3.5" style={{ color: T.primary }} /> {s.l.toUpperCase()}
                      </div>
                      <div className="display font-bold text-2xl mt-1" style={{ color: T.text }}>{s.v}</div>
                    </div>
                  ))}
                </div>

                <div className="rounded-sm p-4 border" style={{ borderColor: T.borderSoft, background: T.cardBgWarm }}>
                  <div className="mono text-[10px] tracking-[0.25em] mb-1" style={{ color: T.textMuted }}>// PREDICTED OVERALL BAND</div>
                  <div className="flex items-end justify-between">
                    <div className="display font-bold text-6xl" style={{ color: T.primary, textShadow: `0 0 18px ${T.primaryGlow}` }}>7.5</div>
                    <div className="mono text-[10px] tracking-[0.25em]" style={{ color: T.text }}>+0.5 ↑ vs last week</div>
                  </div>
                  <div className="mt-3 h-1.5 rounded-sm overflow-hidden" style={{ background: T.borderSoft }}>
                    <div className="h-full" style={{ width: "83%", background: T.primary, boxShadow: `0 0 10px ${T.primaryGlow}` }} />
                  </div>
                </div>

                <div className="mt-4 mono text-[10px] tracking-[0.25em]" style={{ color: T.textMuted }}>
                  <span style={{ color: T.success }}>{">"}</span> next examiner available in <span style={{ color: T.primary }}>00:{String(time.s).padStart(2,"0")}</span>
                </div>
              </div>
            </HudFrame>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SUPPORTED TESTS — strip
      ============================================================ */}
      <section className="border-t border-b" style={{ borderColor: T.border, background: T.bg2 + "70" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="mono text-[10px] tracking-[0.3em]" style={{ color: T.textMuted }}>// SUPPORTED EXAMS</div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            {TESTS.map((t, i) => (
              <div key={i} className="display text-lg font-semibold" style={{ color: T.text }}>
                {t.name} <span className="mono text-xs ml-1" style={{ color: T.primary }}>· {t.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FEATURES
      ============================================================ */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal in">
            <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// CORE SYSTEMS</div>
            <h2 className="display text-4xl md:text-5xl font-bold" style={{ color: T.text }}>
              An <span style={{ color: T.primary }}>AI exam centre</span> in your browser.
            </h2>
            <p className="mt-4 leading-relaxed" style={{ color: T.textMuted }}>
              Six modules. One mission: get you across the band threshold on the first official sitting.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {FEATURES.map((f, i) => (
              <div key={i} className="group relative rounded-sm p-6 transition-all duration-300 hover:-translate-y-1 reveal in"
                style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 16px 40px -20px ${T.primaryGlow}`}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}>
                <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-4 transition-all"
                  style={{ background: T.cardBgWarm, color: T.primary, border: `1px solid ${T.borderSoft}` }}>
                  <f.Icon className="w-5 h-5" />
                </div>
                <div className="display text-xl font-semibold" style={{ color: T.text }}>{f.title}</div>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: T.textMuted }}>{f.desc}</p>
                <div className="mt-5 mono text-[10px] tracking-[0.3em] flex items-center gap-1 transition-all"
                  style={{ color: T.primary }}>
                  EXPLORE <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          REALISM CALLOUT — paper vs CBT
      ============================================================ */}
      <section className="relative py-20" style={{ background: T.bg2 + "80" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="reveal in">
            <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// EXAM REALISM ENGINE</div>
            <h3 className="display text-4xl md:text-5xl font-bold leading-tight" style={{ color: T.text }}>
              Practise the <span style={{ color: T.primary }}>exact</span> exam<br />you&apos;ll sit on test day.
            </h3>
            <p className="mt-4 leading-relaxed" style={{ color: T.textMuted }}>
              We don&apos;t do generic mocks. Pick paper-based or computer-based once — and every drill, every mock, every timer is tuned to that format. Audio plays exactly once. 10-minute transfer. 2-minute review. Just like the real thing.
            </p>
            <ul className="mt-6 space-y-2">
              {[
                "Audio plays only once — no replay, no pause",
                "Realistic transfer time (paper) or review window (CBT)",
                "OCR your handwritten answer sheet on paper-based mocks",
                "Highlight + strikethrough on CBT reading",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: T.text }}>
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: T.primary }} />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-7">
              <PrimaryBtn to="/signup" size="lg">START A FREE MOCK <ArrowRight className="w-4 h-4" /></PrimaryBtn>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 reveal in">
            {[
              { tag: "PAPER-BASED", title: "Print → Transcribe → OCR", desc: "We print an exam-style answer sheet, you write on it, then upload a photo. AI vision extracts every answer." },
              { tag: "COMPUTER-BASED", title: "Live HUD Simulator", desc: "Strict timers, single-play audio, highlight tool, and ~2 min review. Indistinguishable from the real CBT." },
            ].map((c, i) => (
              <HudFrame key={i} className="p-1" color={i === 0 ? T.text : T.primary}>
                <div className="rounded-sm p-6 h-full" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
                  <div className="mono text-[10px] tracking-[0.3em]" style={{ color: i === 0 ? T.text : T.primary }}>// {c.tag}</div>
                  <div className="display text-2xl font-bold mt-2 leading-tight" style={{ color: T.text }}>{c.title}</div>
                  <p className="text-sm mt-3 leading-relaxed" style={{ color: T.textMuted }}>{c.desc}</p>
                </div>
              </HudFrame>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          PRICING — embedded directly on the landing page
      ============================================================ */}
      <section id="pricing" className="relative py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal in">
            <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// PRICING · NO HIDDEN FEES</div>
            <h2 className="display text-4xl md:text-5xl font-bold" style={{ color: T.text }}>
              Two ways to <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>train.</span>
            </h2>
            <p className="mt-4 leading-relaxed" style={{ color: T.textMuted }}>
              Start with a free 5-minute AI assessment. Then pick the plan that fits your timeline.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {[
              {
                id: "free",
                badge: "FREE FOREVER",
                name: "Free Assessment",
                price: "$0",
                cycle: "no card needed",
                Icon: Sparkles,
                blurb: "Take a 5-minute AI Speaking mock and get an instant predicted band.",
                features: ["5-min AI Speaking mock", "Instant band prediction", "Email PDF report"],
                cta: "Start Free Assessment",
                ctaTo: "/free-assessment",
                accent: T.text,
                featured: false,
              },
              {
                id: "self_prep_ai",
                badge: "MOST POPULAR",
                name: "Self Prep AI",
                price: "$14.99",
                cycle: "per month · cancel anytime",
                Icon: Bot,
                blurb: "Unlimited AI examiners across all 4 skills + full mock suite.",
                features: ["Unlimited AI Speaking/Writing/Reading/Listening", "Full IELTS · PTE · ELLT · Duolingo simulators", "AI Coach 24/7 + weakness heatmaps"],
                cta: "Subscribe to Self Prep",
                ctaTo: "/pricing#self_prep_ai",
                accent: T.primary,
                featured: true,
              },
              {
                id: "hybrid",
                badge: "BEST VALUE",
                name: "Hybrid Live + AI",
                price: "$54",
                cycle: "one-time · 8-week course",
                Icon: GraduationCap,
                blurb: "Everything in Self Prep + live online classes with certified tutors.",
                features: ["4 live online classes / week", "Small group · certified tutors", "Money-back if no band improvement"],
                cta: "Enrol in Hybrid",
                ctaTo: "/pricing#hybrid",
                accent: T.secondary,
                featured: false,
              },
            ].map((p) => (
              <div key={p.id} className="reveal in">
              <HudFrame color={p.featured ? p.accent : T.borderSoft} className={p.featured ? "p-1.5" : "p-1"}>
                <div className="rounded-sm h-full flex flex-col p-7"
                  style={{
                    background: p.featured ? `linear-gradient(180deg, ${T.cardBgWarm} 0%, ${T.cardBg} 80%)` : T.cardBg,
                    border: `1px solid ${p.featured ? p.accent : T.borderSoft}`,
                    boxShadow: p.featured ? `0 30px 60px -30px ${T.primaryGlow}` : "0 4px 14px -8px rgba(0,48,73,0.10)",
                  }}
                  data-testid={`landing-price-${p.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                      style={{ background: `${p.accent}15`, color: p.accent, border: `1px solid ${p.accent}40` }}>
                      <p.Icon className="w-6 h-6" />
                    </div>
                    <div className="mono text-[9px] tracking-[0.3em] px-2 py-1 rounded-sm"
                      style={{ background: p.featured ? p.accent : T.bg2, color: p.featured ? T.textOnPrimary : T.textMuted }}>
                      {p.badge}
                    </div>
                  </div>
                  <div className="display text-3xl font-bold mb-1" style={{ color: T.text }}>{p.name}</div>
                  <p className="text-sm mb-5" style={{ color: T.textMuted }}>{p.blurb}</p>
                  <div className="mb-5 pb-5 border-b" style={{ borderColor: T.borderSoft }}>
                    <div className="display font-bold text-5xl" style={{ color: p.accent, textShadow: p.featured ? `0 0 18px ${T.primaryGlow}` : "none" }}>{p.price}</div>
                    <div className="mono text-[10px] tracking-[0.25em] mt-1" style={{ color: T.textMuted }}>{p.cycle}</div>
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: T.text }}>
                        <span className="w-4 h-4 rounded-sm flex items-center justify-center shrink-0 mt-0.5"
                          style={{ background: p.accent, color: "#fff" }}>
                          <Check className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={p.ctaTo}
                    data-testid={`landing-price-cta-${p.id}`}
                    className="w-full h-14 rounded-sm font-bold uppercase tracking-[0.2em] text-sm transition-all duration-300 active:scale-[0.97] inline-flex items-center justify-center gap-2"
                    style={{
                      background: p.featured ? p.accent : "transparent",
                      color: p.featured ? T.textOnPrimary : p.accent,
                      border: `2px solid ${p.accent}`,
                      boxShadow: p.featured ? `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)` : "none",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                    onMouseEnter={(e) => {
                      if (!p.featured) {
                        e.currentTarget.style.background = p.accent;
                        e.currentTarget.style.color = T.textOnPrimary;
                      } else {
                        e.currentTarget.style.boxShadow = `0 0 32px ${T.primaryGlow}, inset 0 0 14px rgba(255,255,255,0.28)`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!p.featured) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = p.accent;
                      } else {
                        e.currentTarget.style.boxShadow = `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)`;
                      }
                    }}
                  >
                    {p.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </HudFrame>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/pricing" className="mono text-[11px] tracking-[0.3em] uppercase hover:underline" style={{ color: T.textMuted }}>
              See full pricing & FAQ →
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          TESTIMONIALS
      ============================================================ */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal in">
            <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// CANDIDATE LOGS</div>
            <h2 className="display text-4xl md:text-5xl font-bold" style={{ color: T.text }}>
              Real people. <span style={{ color: T.primary }}>Real bands.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-sm p-6 reveal in" style={{ background: T.cardBgWarm, border: `1px solid ${T.borderSoft}` }}>
                <Quote className="w-6 h-6" style={{ color: T.primary }} />
                <p className="mt-3 leading-relaxed" style={{ color: T.text }}>“{t.body}”</p>
                <div className="mt-5 pt-4 border-t" style={{ borderColor: T.borderSoft }}>
                  <div className="display font-semibold" style={{ color: T.text }}>{t.name}</div>
                  <div className="mono text-[10px] tracking-[0.25em] mt-0.5" style={{ color: T.primary }}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
      ============================================================ */}
      <section className="relative py-20 lg:py-24" style={{ background: `linear-gradient(135deg, ${T.bg2} 0%, ${T.cream}66 100%)` }}>
        <GridBG intensity={0.04} />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="display text-4xl md:text-6xl font-bold leading-[1.02]" style={{ color: T.text }}>
            Stop practising. <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>Start passing.</span>
          </h2>
          <p className="mt-5 text-lg" style={{ color: T.textMuted }}>
            Your first mock test, AI Speaking session and band prediction are free.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-9">
            <PrimaryBtn to="/signup" size="xl" data-testid="footer-cta-signup">
              CREATE FREE PROFILE <ArrowRight className="w-5 h-5" />
            </PrimaryBtn>
            <OutlineBtn to="/login" size="lg" data-testid="footer-cta-login">
              SIGN IN
            </OutlineBtn>
          </div>
        </div>
      </section>

      {/* ============================================================
          FOOTER — all secondary links live here
      ============================================================ */}
      <footer style={{ background: T.text, color: "#E5E9EE" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm border-2 flex items-center justify-center display font-bold text-lg"
                style={{ borderColor: T.primary, color: T.primary, boxShadow: `0 0 14px ${T.primaryGlow}` }}>E</div>
              <div>
                <div className="display font-bold text-xl tracking-tight text-white">EnglishTest<span style={{ color: T.primary }}>.UK</span></div>
                <div className="mono text-[9px] tracking-[0.35em] mt-0.5 text-white/60">// PASS · NOT JUST PRACTICE</div>
              </div>
            </div>
            <p className="text-sm text-white/65 leading-relaxed max-w-sm">
              The AI training ground for IELTS, PTE, Oxford ELLT and Duolingo English Test candidates. Built in the UK, used worldwide.
            </p>
            <div className="mt-5 flex items-center gap-2 mono text-[10px] tracking-[0.3em] text-white/60">
              <Globe className="w-3.5 h-3.5" /> EN · हिन्दी · العربية · 中文 · Español
            </div>
          </div>

          {[
            { h: "PLATFORM", links: [["IELTS Prep", "/app/ielts/start"], ["PTE Academic", "/app/pte"], ["Oxford ELLT", "/app/oxford"], ["Duolingo", "/app/duolingo"]] },
            { h: "PRACTICE", links: [["Listening", "/app/tests"], ["Reading", "/app/tests"], ["Writing", "/app/writing"], ["Speaking Room", "/app/speaking-room"]] },
            { h: "COMPANY",  links: [["Pricing", "/pricing"], ["Book a free trial", "/trial"], ["Live classrooms", "/app/classrooms"], ["AI Coach", "/app/coach"]] },
            { h: "LEGAL",    links: [["Privacy policy", "/"], ["Terms of service", "/"], ["Refund policy", "/"], ["Contact support", "/"]] },
          ].map((col, i) => (
            <div key={i}>
              <div className="mono text-[10px] tracking-[0.3em] text-white/55 mb-4">{col.h}</div>
              <ul className="space-y-2.5">
                {col.links.map((l, j) => (
                  <li key={j}>
                    <Link to={l[1]} className="text-sm text-white/85 hover:text-white transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {l[0]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="mono text-[10px] tracking-[0.3em] text-white/55">
              © 2026 EnglishTest.UK · ALL SYSTEMS NOMINAL
            </div>
            <div className="flex gap-2">
              <OutlineBtn to="/login" size="sm" className="!text-white !border-white/40 hover:!border-[var(--p)]" style={{ "--p": T.primary }}>SIGN IN</OutlineBtn>
              <PrimaryBtn to="/signup" size="sm">SIGN UP</PrimaryBtn>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function getTimeLeft() {
  const now = new Date();
  return { s: (59 - now.getSeconds() + 60) % 60 };
}
